import React, { useContext } from 'react'
import { getUser } from '@fs/zion-user'
import { useTranslation } from 'react-i18next'
import * as queueService from '../../../../services/QueuesService'
import * as dmqueuesService from '../../../../services/dmqueuesService'
import useNotifications from '../../../../hooks/useNotifications'
import { QueuesContext } from '../../QueuesContext'
import { recycleRejectedTempleNames, releaseReservations } from '../../../../services/reservationsService'
import { getReservationData } from '../../../../services/personService'
import { HolocaustDialog } from '../buttonDialogs/HolocaustDialog'
import { NotHolocaustDialog } from '../buttonDialogs/NotHolocaustDialog'
import { FamousDialog } from '../buttonDialogs/FamousDialog'

export default () => {}

export function useQueueButtons() {
  const { t } = useTranslation()
  const notifications = useNotifications()
  const { queueButtonYadVashem } = useContext(QueuesContext)

  // Helper to convert camelCase to space-separated words
  const toDisplayName = (str) => str.replaceAll(/([A-Z])/g, ' $1').trim()

  /**
   * Generic handler for queue button actions that follow the same pattern
   * @param {Object} params - Parameters object
   * @param {string} params.action - The QUEUE_ACTIONS constant value
   * @param {string} params.resolution - The resolution string to return
   * @param {string} params.personId - The person ID to act upon
   * @param {Object} params.message - The message object
   * @param {Object} params.person - The person object
   * @param {Function} params.getExtraParams - Optional function to get extra parameters for the action
   */
  const createQueueButtonHandler = async ({
    action,
    resolution,
    personId,
    message,
    person = null,
    getExtraParams = null,
  }) => {
    const user = await getUser()

    // Build arguments array - only include extraParams if it's defined
    const actionArgs = getExtraParams
      ? [action, personId, user.cisId, getExtraParams(message)]
      : [action, personId, user.cisId]

    return queueService
      .queueButtonAction(...actionArgs)
      .then(() => {
        if (isPrincipal(message, person)) {
          return { messageId: message.id, resolution }
        }
        return {}
      })
      .catch((err) => {
        if (err?.response?.status === 503) {
          throw new Error(
            t('queues.failedToPerform503', {
              defaultValue: `{action} action for message: {messageId} Person copy did not complete completely. Please check the person record {personId} to verify the action was performed.`,
              action: 'Sensitive',
              messageId: message.id,
              personId,
            })
          )
        }

        throw new Error(
          t(`queues.failedToPerform${resolution}Action`, {
            defaultValue: `Failed to perform "${toDisplayName(resolution)}" action for message: ${message.id}`,
            messageId: message.id,
          })
        )
      })
  }

  const queueButtonsDialogs = {
    holocaustDialog: (message, personId, personName, onSubmitCallback, onCancelCallback) => {
      return (
        <HolocaustDialog
          message={message}
          personId={personId}
          personName={personName}
          onSubmitCallback={onSubmitCallback}
          onCancelCallback={onCancelCallback}
        />
      )
    },
    notHolocaustDialog: (message, personId, personName, onSubmitCallback, onCancelCallback) => {
      return (
        <NotHolocaustDialog
          message={message}
          personId={personId}
          personName={personName}
          onSubmitCallback={onSubmitCallback}
          onCancelCallback={onCancelCallback}
        />
      )
    },
    famousDialog: (message, personId, personName, onSubmitCallback, onCancelCallback) => {
      return (
        <FamousDialog
          message={message}
          personId={personId}
          personName={personName}
          onSubmitCallback={onSubmitCallback}
          onCancelCallback={onCancelCallback}
        />
      )
    },
  }

  // Configuration object - only specify what's different from the defaults
  // Key is the button name suffix, action is derived from key (Famous → 'famous')
  const queueButtonConfig = {
    NotFamous: {
      action: 'notFamous',
    },
    Deity: {
      action: 'deity',
    },
    ReligiousTitle: {
      action: 'religiousTitle',
    },
    NotReligiousTitle: {
      action: 'notReligiousTitle',
    },
    Historical: {
      action: 'historical',
      getPersonId: (msg) => msg.details.matchedPersonId,
      getExtraParams: (msg) => msg.details.historicalType,
    },
    NotHistorical: {
      action: 'notHistorical',
      getPersonId: (msg) => msg.details.matchedPersonId,
    },
    Transition: {
      action: 'transition',
    },
    NotTransition: {
      action: 'notTransition',
    },
    HybridPublic: {
      action: 'hybridPublic',
    },
    HybridPrivate: {
      action: 'hybridPrivate',
    },
    NotHybrid: {
      action: 'notHybrid',
    },
    Sensitive: {
      action: 'sensitive',
    },
    NotSensitive: {
      action: 'notSensitive',
    },
    Mythological: {
      action: 'mythological',
    },
    NotMythological: {
      action: 'notMythological',
    },
    Fictitious: {
      action: 'fictitious',
    },
    NotFictitious: {
      action: 'notFictitious',
    },
  }

  // Generate all queue button handlers from config
  const generatedHandlers = Object.entries(queueButtonConfig).reduce((handlers, [name, config]) => {
    handlers[`queueButton${name}`] = async ({ message, person = null }) => {
      const personId = person?.personId || (config.getPersonId ? config.getPersonId(message) : message.details.personId)
      return createQueueButtonHandler({
        resolution: name,
        action: config.action,
        personId,
        message,
        person,
        getExtraParams: config.getExtraParams,
      })
    }
    return handlers
  }, {})

  const queueButtons = {
    ...generatedHandlers,
    queueButtonRecycle: async ({ message, queuesData }) => {
      const personId = queuesData?.osid?.[message?.details?.osid]?.personId
      if (personId) {
        return recycleRejectedTempleNames(personId)
          .then(() => ({ messageId: message.id, resolution: 'Recycle' }))
          .catch((err) => {
            if (err?.response?.status === 404) {
              notifications.error(
                t(
                  'queues.recycle.failedToRecycleRejectedTempleListNameNotFound',
                  `Failed to recycle rejected temple list name for personId: {personId}. Not found.`,
                  { personId }
                )
              )
            } else {
              notifications.error(
                t(
                  'queues.recycle.failedToRecycleRejectedTempleListName',
                  `Failed to recycle rejected temple list name for personId: {personId}`,
                  { personId }
                )
              )
            }
          })
      }
      return null
    },
    queueButtonUnreserve: async ({ message, queuesData }) => {
      const personId = queuesData.osid[message.details.osid].personId
      return getReservationData(personId)
        .then((response) => {
          const reservationIds = response.data.reservations.map((reservation) => reservation.ranId)
          if (reservationIds?.length > 0) {
            return releaseReservations(reservationIds)
              .then(() => ({ messageId: message.id, resolution: 'Unreserve' }))
              .catch(() => {
                notifications.error(
                  t('queues.recycle.failedToUnreserveRejectedPersonReservations', {
                    defaultValue: `Failed to unreserve reservations from rejected temple list person: ${personId}`,
                    personId,
                  })
                )
              })
          }
          return { messageId: message.id, resolution: 'Unreserve' }
        })
        .catch((err) => {
          notifications.error(
            t('person.reservations.errorFetchingReservations', `Error retrieving reservation data`),
            err
          )
        })
    },
    deleteMessage: async ({ message, resolution = 'delete' }) => {
      return { messageId: message.id, resolution }
    },
    deleteMessageById: async ({ message, resolution = 'delete' }) => {
      return dmqueuesService.deleteMessageById(message.id, resolution).catch((err) => {
        notifications.error(
          t('queues.failedToDeleteMessage', {
            defaultValue: `Failed to delete message ID: ${message.id}`,
            messageId: message.id,
          }),
          err
        )
      })
    },
    queueButtonForebears: ({ message, person, queuesData }) => {
      const names = (person || queuesData?.[message?.details?.personId])?.name?.split(' ')
      const lastName = names.length > 0 ? names[names.length - 1] : person.name
      const url = `https://forebears.io/surnames?q=${lastName}`
      window.open(url, 'forebearsTab')
      return url
    },
    queueButtonYadVashem: async ({ person }) => {
      return queueButtonYadVashem(person)
    },
  }

  return { queueButtons, queueButtonsDialogs }
}

function isPrincipal(message, person) {
  if (person == null) {
    return true
  }
  if (message?.details?.personId) {
    return person.personId === message.details.personId
  }
  if (message?.details?.matchedPersonId) {
    return person.personId === message.details.matchedPersonId
  }
  return false
}
